import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useToken } from '../../../shared/hooks';
import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { usePaymentInfo } from '../hooks/usePaymentInfo';
import { buildPaymentCompletePath, resolvePaymentSuccessPath } from '../utils/postPaymentNavigate';

/**
 * 支払情報の取得を待つ上限時間（ms）。
 * API クライアントにタイムアウトがないため、getPayment が返らない場合でも
 * Flutter ローディングのまま無限待機にならないよう、超過時は決済完了画面へフォールバックする。
 */
export const PAYMENT_INFO_FALLBACK_TIMEOUT_MS = 10000;

/**
 * 決済成功コールバック受け口
 *
 * GMO / NP 後払いからのコールバックで /success?inquiryId=xxx に遷移してくる。
 * inquiryId は本物の inquiry_id（doctor 側実装と同仕様）。
 *
 * 支払方法を取得し、遷移先は resolvePaymentSuccessPath（課題表381）で決める:
 * クレジットカードは領収書発行画面へ、それ以外と取得失敗時は従来どおり決済完了画面へ
 * （領収書の導線が取れなくてもアンケート導線は塞がない）。
 *
 * NP 後払いは外部ドメインを経由するため sessionStorage が消える可能性があり、
 * URL の inquiryId に依拠する形に統一している。
 * リダイレクト中は画面を描画せず Flutter 側のローディングをそのまま表示する。
 * 取得が PAYMENT_INFO_FALLBACK_TIMEOUT_MS を超えた場合も決済完了画面へフォールバックする。
 */
const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useToken();
  const inquiryId = searchParams.get('inquiryId');
  const tokenParam = searchParams.get('token');
  const [hasError, setHasError] = useState(false);

  const { paymentInfo, isLoading } = usePaymentInfo(inquiryId ?? '', token);
  // 取得完了とタイムアウトのどちらが先でも遷移は 1 回だけにする
  const hasNavigatedRef = useRef(false);

  // エラー表示のときだけ Flutter ローディングを解除する（リダイレクト中は ref を付けないので維持される）
  const contentRef = useWebViewLoading(false);

  useEffect(() => {
    if (!inquiryId) {
      setHasError(true);
      return;
    }
    if (hasNavigatedRef.current) {
      return;
    }

    if (isLoading) {
      // 取得が長引いた場合のフォールバック（取得完了で isLoading が変わればクリーンアップで破棄される）
      const timer = setTimeout(() => {
        hasNavigatedRef.current = true;
        navigate(buildPaymentCompletePath(inquiryId, tokenParam), { replace: true });
      }, PAYMENT_INFO_FALLBACK_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }

    hasNavigatedRef.current = true;
    navigate(resolvePaymentSuccessPath(inquiryId, paymentInfo?.paymentMethodCode, tokenParam), {
      replace: true,
    });
  }, [inquiryId, tokenParam, isLoading, paymentInfo, navigate]);

  if (hasError) {
    return (
      <div
        ref={contentRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: '16px',
        }}
      >
        <p>エラーが発生しました</p>
        <p style={{ fontSize: '14px', color: '#666' }}>決済情報が見つかりませんでした</p>
      </div>
    );
  }

  return null;
};

export default PaymentSuccess;
